import { H3, StyledAlert } from "App/global-style";
import {
  getCollectionOfDittoComponents,
  useTexts,
} from "Components/TextContext";
import { ELECTRIC_SCOOTER_DEFAULT_DEDUCTIBLE, getDeductible } from "../utils";

import { CurrentPriceMessage } from "Components/CurrentPriceMessage/CurrentPriceMessage";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { MultipleProductCoverages } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/MultipleProductCoverages";
import { Description } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/Coverages/coverages.styles";
import { AccessibleCallUsMessage } from "Components/Form/AccessibleCallUsMessage";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { useFeatureToggle } from "Store/featureToggles/featureToggleHooks";
import { FeatureToggle } from "Store/featureToggles/featureToggleTypes";
import { ElectricScooterCoverage } from "Store/interfaces/products/coverage";
import { useCoveragePriceCalculations } from "Store/priceCalculation/priceCalculationHooks";
import { ReduxState } from "Store/rootReducer";
import { TextLanguage } from "Store/texts/interfaces";
import { useFormikContext } from "formik";
import React from "react";
import { useSelector } from "react-redux";
import { ElectricScooterTexts } from "../interface";
import { ElectricScooterProductPriceFormValues } from "./interface";

interface Props {
  readonly texts: ElectricScooterTexts;
  readonly lang: TextLanguage;
}

export const Coverages = (props: Props): React.ReactElement => {
  const disableKaskoForElectricScooter = useFeatureToggle(
    FeatureToggle.SSX_513,
  );
  const formProps = useFormikContext<ElectricScooterProductPriceFormValues>();
  const { texts, lang } = props;
  const { getText } = useTexts();
  const { isLoading } = useSelector(
    (state: ReduxState) => state.priceCalculation.priceCalculationResult,
  );
  const { electricscooter: coverageCardTextComponents } =
    getCollectionOfDittoComponents(TextComponentNameLevel1.COVERAGE_CARD);
  const coverages = useCoveragePriceCalculations();

  if (coverages.length === 0) {
    return <SystemErrorAlert alertId="no-coverages" />;
  }

  const getDisabledKaskoDesc = (): React.ReactNode => {
    return (
      <>
        <Description fw={600} size={"16px"}>
          {getText(
            "coveragecard.electricscooter.coveragelabel-electric_scooter_full_own_damage_disabled",
          )}
        </Description>

        <Description fw={400} size={"14px"}>
          <p>
            {getText(
              "coveragecard.electricscooter.description-electric_scooter_full_own_damage_disabled",
            )}
          </p>
          <br />
          <AccessibleCallUsMessage
            customSuffix={getText(
              "coveragecard.electricscooter.description-electric_scooter_full_own_damage_call_us_disabled",
            )}
          />
        </Description>
      </>
    );
  };

  return (
    <>
      <StyledAlert
        type="info"
        title={texts.tile.productPrice.deductibleInfo[lang]}
        fluid
      />
      <H3>{texts.formText.checkPrice.coverage.label[lang]}</H3>
      <MultipleProductCoverages
        fieldName="coverage"
        product={ProductBackendName.ELECTRIC_SCOOTER}
        coverages={coverages}
        coverageStandards={Object.values(ElectricScooterCoverage)}
        priceIsLoading={isLoading}
        textComponents={coverageCardTextComponents}
        extensions={{
          [ElectricScooterCoverage.ELECTRIC_SCOOTER_LIABILITY]:
            ElectricScooterCoverage.ELECTRIC_SCOOTER_LIABILITY_WITH_ACCIDENT,
          [ElectricScooterCoverage.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE]:
            ElectricScooterCoverage.ELECTRIC_SCOOTER_REDUCED_OWN_DAMAGE_WITH_ACCIDENT,
          [ElectricScooterCoverage.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE]:
            ElectricScooterCoverage.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE_WITH_ACCIDENT,
        }}
        disabledCoverages={
          disableKaskoForElectricScooter
            ? {
                [ElectricScooterCoverage.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE]:
                  getDisabledKaskoDesc(),
                [ElectricScooterCoverage.ELECTRIC_SCOOTER_FULL_OWN_DAMAGE_WITH_ACCIDENT]:
                  getDisabledKaskoDesc(),
              }
            : undefined
        }
      />
      <CurrentPriceMessage
        productBackendName={ProductBackendName.ELECTRIC_SCOOTER}
        coverages={coverages}
        selectedPrices={[
          {
            selectedCoverage: formProps.values.coverage,
            selectedDeductible: getDeductible(formProps.values.coverage),
            defaultDeductible: ELECTRIC_SCOOTER_DEFAULT_DEDUCTIBLE,
          },
        ]}
      />
    </>
  );
};
